import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import { fetchDbSummary, fetchDbObjects, vacuumDbFn, refreshCaggsFn } from '@/api/endpoints';
import { Btn, fmtBytes, ObjectType, Section, StatCard, TypeBadge } from '@/components/db/parts';
import type { DbObject } from '@/api/types';

type SortKey = 'name' | 'type' | 'total_bytes';
type SortDir = 'asc' | 'desc';

// Filter + sort live in the URL query string so the browser back button restores
// them when returning from /db/:schema/:name (the page unmounts on navigation).
const ALL_TYPES: readonly ObjectType[] = ['table', 'hypertable', 'cagg'];
const SORT_KEYS: readonly SortKey[] = ['name', 'type', 'total_bytes'];

const TYPE_LABEL: Record<ObjectType, string> = {
  table: 'Tables',
  hypertable: 'Hypertables',
  cagg: 'Caggs',
};

const TYPE_DOT: Record<ObjectType, string> = {
  table: 'bg-slate-400',
  hypertable: 'bg-indigo-500',
  cagg: 'bg-teal-500',
};

export default function Db({ device }: { device: Device }) {
  const toast = useToast();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const filters = useMemo<Set<ObjectType>>(() => {
    const raw = params.get('types');
    if (!raw) return new Set(ALL_TYPES);
    const kept = ALL_TYPES.filter((t) => raw.split(',').includes(t));
    return new Set(kept.length ? kept : ALL_TYPES);
  }, [params]);

  const sortKeyRaw = params.get('sort');
  const sortKey: SortKey = SORT_KEYS.includes(sortKeyRaw as SortKey) ? (sortKeyRaw as SortKey) : 'total_bytes';
  const sortDir: SortDir = params.get('dir') === 'asc' ? 'asc' : 'desc';

  // replace: true — filter/sort clicks must not pile up history entries, or "back"
  // would step through every toggle instead of leaving the page.
  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const { data: summary, isError: isSummaryError, error: summaryError } = useQuery({ queryKey: ['db', 'summary'], queryFn: fetchDbSummary });
  const { data: objectsResp, isLoading, isError: isObjectsError, error: objectsError } = useQuery({ queryKey: ['db', 'objects'], queryFn: fetchDbObjects });
  const objects: DbObject[] = objectsResp?.objects ?? [];

  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'superAdmin';

  const onVacuumSuccess = (res: { message: string; priorite: number }) => {
    toast.push(`${res.message} (priorité ${res.priorite})`, 'success');
    qc.invalidateQueries({ queryKey: ['db'] });
  };

  const vacuumQuickMut = useConfirmedMutation(
    (_v: void) => vacuumDbFn(false),
    {
      title: 'VACUUM rapide',
      message: 'Confirmer le VACUUM rapide ? (réclame l’espace mort, lock léger)',
      onSuccess: onVacuumSuccess,
    },
  );

  const vacuumFullMut = useConfirmedMutation(
    (_v: void) => vacuumDbFn(true),
    {
      title: 'VACUUM FULL',
      message: 'Confirmer le VACUUM ? (FULL prend un lock exclusif et peut être long)',
      destructive: true,
      onSuccess: onVacuumSuccess,
    },
  );

  const caggMut = useConfirmedMutation(
    (scope: 'recent' | 'all') => refreshCaggsFn(scope),
    {
      title: 'Refresh des CAGGs',
      message: 'Confirmer le refresh des agrégats continus ? (mis en queue côté ingest)',
      onSuccess: (res) => {
        toast.push(`${res.message} (priorité ${res.priorite})`, 'success');
        qc.invalidateQueries({ queryKey: ['db'] });
      },
    },
  );

  const visible = useMemo(() => {
    let rows = objects.filter((o) => filters.has(o.type));
    rows = [...rows].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'total_bytes') {
        return ((a.total_bytes ?? 0) - (b.total_bytes ?? 0)) * dir;
      }
      if (sortKey === 'type') return a.type.localeCompare(b.type) * dir;
      return `${a.schema}.${a.name}`.localeCompare(`${b.schema}.${b.name}`) * dir;
    });
    return rows;
  }, [objects, filters, sortKey, sortDir]);

  const toggleFilter = (t: ObjectType) => {
    const next = new Set(filters);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    const kept = next.size === 0 ? ALL_TYPES : ALL_TYPES.filter((x) => next.has(x));
    // Omit the param when everything is selected — keeps the default URL clean.
    patchParams({ types: kept.length === ALL_TYPES.length ? null : kept.join(',') });
  };

  const toggleSort = (k: SortKey) => {
    const dir: SortDir = sortKey === k
      ? (sortDir === 'asc' ? 'desc' : 'asc')
      : (k === 'name' ? 'asc' : 'desc');
    patchParams({
      sort: k === 'total_bytes' ? null : k,
      dir: dir === 'desc' ? null : dir,
    });
  };

  const isError = isSummaryError || isObjectsError;
  const error = summaryError ?? objectsError;

  if (isError) {
    return (
      <PageShell device={device} title="Base de données" breadcrumb="Serveur BD">
        <div className="p-8 text-center">
          <p className="text-destructive font-medium">Erreur de chargement</p>
          <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
        </div>
      </PageShell>
    );
  }

  if (isLoading || !summary) {
    return (
      <PageShell device={device} title="Base de données" breadcrumb="Serveur BD">
        <div className="p-8 text-muted-foreground">Chargement…</div>
      </PageShell>
    );
  }

  return (
    <PageShell device={device} title="Base de données" breadcrumb="Serveur BD">
      <div className="p-4 md:p-6 space-y-4">
        {/* Header KPIs */}
        <Section
          title="Vue d'ensemble"
          action={
            <span className="text-[11px] text-muted-foreground">
              MAJ: {new Date(summary.fetched_at).toLocaleString('fr-FR')}
            </span>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Objets totaux"
              value={summary.total_objects}
              sub={
                <span className="flex flex-col gap-0.5 mt-1">
                  <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-sm ${TYPE_DOT.hypertable}`} /> Hypertables: <strong>{summary.hypertables}</strong>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-sm ${TYPE_DOT.table}`} /> Tables: <strong>{summary.tables}</strong>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-sm ${TYPE_DOT.cagg}`} /> Caggs: <strong>{summary.caggs}</strong>
                  </span>
                </span>
              }
            />
            <StatCard
              label="Taille on-disk"
              value={fmtBytes(summary.on_disk_bytes)}
              tone="primary"
              sub={<span>Original: <strong>{fmtBytes(summary.original_bytes)}</strong></span>}
            />
            <StatCard
              label="Compression"
              value={`${summary.compression_savings_pct}%`}
              tone="success"
            />
            <StatCard
              label="Objets compressés"
              value={objects.filter((o) => o.is_compressed).length}
              sub="is_compressed = true"
            />
          </div>
        </Section>

        {/* Actions globales */}
        <Section title="Actions globales">
          <div className="flex flex-wrap gap-2">
            <Btn variant="default" onClick={() => vacuumQuickMut.mutate()}>
              VACUUM rapide
            </Btn>
            {isSuperAdmin && (
              <Btn variant="destructive" onClick={() => vacuumFullMut.mutate()}>
                VACUUM FULL (superAdmin)
              </Btn>
            )}
            <Btn variant="primary" onClick={() => caggMut.mutate('recent')}>
              ↻ Refresh CAGGs (30j)
            </Btn>
            {isSuperAdmin && (
              <Btn variant="primary" onClick={() => caggMut.mutate('all')}>
                ↻ Refresh CAGGs (tout l’historique)
              </Btn>
            )}
          </div>
        </Section>

        {/* Filtres + liste */}
        <Section
          title="Objets"
          action={
            <div className="flex items-center gap-1.5">
              {(['hypertable', 'table', 'cagg'] as ObjectType[]).map((t) => {
                const active = filters.has(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleFilter(t)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition ${
                      active
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-sm ${TYPE_DOT[t]}`} />
                    {TYPE_LABEL[t]}
                  </button>
                );
              })}
            </div>
          }
        >
          <div className="border border-border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[11px] uppercase text-muted-foreground">
                <tr>
                  <SortHead k="name" label="Nom" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortHead k="type" label="Type" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortHead k="total_bytes" label="Taille" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                  <th className="w-8 px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visible.map((o) => (
                  <tr
                    key={`${o.schema}.${o.name}`}
                    className="hover:bg-accent/40 cursor-pointer"
                    onClick={() => nav(`/db/${o.schema}/${o.name}`)}
                  >
                    <td className="px-3 py-2.5 font-mono text-xs">
                      {o.schema}.<span className="font-semibold">{o.name}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <TypeBadge type={o.type} />
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtBytes(o.total_bytes)}</td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground">→</td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground text-xs">
                      Aucun objet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </PageShell>
  );
}

function SortHead({
  k,
  label,
  sortKey,
  sortDir,
  onClick,
  align,
}: {
  k: SortKey;
  label: string;
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
  align?: 'right';
}) {
  return (
    <th
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none hover:bg-muted`}
      onClick={() => onClick(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );
}
